import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function TestManagementPage() {
    const navigate = useNavigate();
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const getTestTypeName = (type) => {
        switch (type) {
            case 'mcq': return 'Trắc nghiệm';
            case 'code':
            case 'practical': return 'Thực hành/Code';
            case 'essay': return 'Tự luận';
            case 'iq': return 'IQ/Logic';
            default: return type || 'Khác';
        }
    };

    useEffect(() => {
        const fetchTests = async () => {
            try {
                const token = localStorage.getItem('skillmatch_token');
                const API_URL = process.env.REACT_APP_API_BASE_URL;
                const response = await fetch(`${API_URL}/api/tests/my-tests`, { headers: { 'x-auth-token': token } });
                if (!response.ok) throw new Error('Failed');
                const data = await response.json();
                setTests(data);
            } catch (error) { console.error(error); } finally { setLoading(false); }
        };
        fetchTests();
    }, []);

    const getStatusBadge = (test) => {
        if (test.status === 'DRAFT') return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600 border border-gray-200">Bản nháp</span>;
        if (test.isPublic === false && test.status === 'PUBLISHED') return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700 border border-indigo-200"><span className="material-symbols-outlined text-[14px] mr-1">lock</span> Private</span>;
        if (test.status === 'PUBLISHED') return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">Đang mở</span>;
        return null;
    };

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('vi-VN');

    return (
        <div className="space-y-6">

            {/* HEADER */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Kho bài kiểm tra</h1>
                    <p className="text-gray-500 text-sm mt-1">Quản lý các đề thi đánh giá năng lực ứng viên.</p>
                </div>
                <Link to="/business/tests/new" className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md">
                    <span className="material-symbols-outlined">add</span> Tạo bài test mới
                </Link>
            </div>

            {/* TABLE */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-gray-400">Đang tải dữ liệu...</div>
                ) : tests.length === 0 ? (
                    <div className="p-16 text-center">
                        <div className="mx-auto w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-gray-400 text-3xl">quiz</span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Chưa có bài kiểm tra</h3>
                        <p className="text-gray-500 mt-2 mb-6">Tạo bài test để sàng lọc ứng viên tự động.</p>
                        <Link to="/business/tests/new" className="text-blue-600 font-bold hover:underline">Tạo ngay</Link>
                    </div>
                ) : (
                    <table className="min-w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 font-semibold text-gray-600">Tên bài kiểm tra</th>
                                <th className="px-6 py-4 font-semibold text-gray-600">Loại</th>
                                <th className="px-6 py-4 font-semibold text-gray-600">Thời lượng</th>
                                <th className="px-6 py-4 font-semibold text-gray-600">Ngày tạo</th>
                                <th className="px-6 py-4 font-semibold text-gray-600">Trạng thái</th>
                                <th className="px-6 py-4 font-semibold text-gray-600 text-right">Hành động</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {tests.map((test) => (
                                <tr key={test._id} className="hover:bg-blue-50/30 transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[18px]">
                                                    {test.testType === 'coding' ? 'code' : 'check_circle'}
                                                </span>
                                            </div>
                                            <Link to={`/business/tests/edit/${test._id}`} className="font-bold text-gray-900 hover:text-blue-600 transition-colors">
                                                {test.assessmentName}
                                            </Link>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="uppercase text-xs font-bold text-gray-500 tracking-wider border border-gray-200 px-2 py-1 rounded bg-gray-50">
                                            {getTestTypeName(test.testType)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-gray-700 font-medium">
                                        {test.timeLimit} phút
                                    </td>
                                    <td className="px-6 py-4 text-gray-500">
                                        {formatDate(test.createdAt)}
                                    </td>
                                    <td className="px-6 py-4">
                                        {getStatusBadge(test)}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <Link to={`/business/tests/edit/${test._id}`} className="text-gray-400 hover:text-blue-600 p-2 rounded-full hover:bg-blue-50 transition-colors inline-block">
                                            <span className="material-symbols-outlined text-[20px]">edit</span>
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}